import React, { useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Loader2, Copy, Check, Image as ImageIcon } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { LANGUAGES } from "@/lib/constants"
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, FacebookIcon, TwitterIcon, LinkedinIcon } from 'react-share'

// Based on research of most effective non-profit social media content types
const contentTypes = [
  {
    id: "impact_story",
    title: "Impact Story",
    description: "Share a specific story about how your work has made a difference",
    platforms: ["facebook", "instagram", "linkedin"],
    imagePrompt: "Compelling photo of beneficiary or project outcome"
  },
  {
    id: "fundraising_campaign",
    title: "Fundraising Campaign",
    description: "Launch or update about a fundraising campaign",
    platforms: ["facebook", "instagram", "twitter"],
    imagePrompt: "Visual representation of campaign goal or progress"
  },
  {
    id: "volunteer_spotlight",
    title: "Volunteer Spotlight",
    description: "Highlight volunteers and their contributions",
    platforms: ["instagram", "linkedin", "facebook"],
    imagePrompt: "Photo of volunteer in action or portrait"
  },
  {
    id: "event_promotion",
    title: "Event Promotion",
    description: "Promote upcoming events or share event highlights",
    platforms: ["instagram", "facebook", "twitter"],
    imagePrompt: "Event banner or previous event photos"
  },
  {
    id: "behind_scenes",
    title: "Behind the Scenes",
    description: "Show day-to-day operations and team work",
    platforms: ["instagram", "tiktok", "facebook"],
    imagePrompt: "Candid shots of team working or project in progress"
  },
  {
    id: "stats_impact",
    title: "Stats & Impact",
    description: "Share statistics and measurable impact",
    platforms: ["linkedin", "twitter", "facebook"],
    imagePrompt: "Infographic showing key statistics and achievements"
  },
  {
    id: "gratitude_post",
    title: "Thank You Post",
    description: "Express gratitude to donors, volunteers, or partners",
    platforms: ["facebook", "linkedin", "instagram"],
    imagePrompt: "Heartwarming image of community or thank you graphic"
  },
  {
    id: "awareness_education",
    title: "Awareness & Education",
    description: "Educate about your cause or important issues",
    platforms: ["instagram", "linkedin", "twitter"],
    imagePrompt: "Educational infographic or illustrative image"
  }
]

const platforms = [
  {
    id: "facebook",
    label: "Facebook",
    aspectRatio: "16:9"  // Landscape format for feed
  },
  {
    id: "instagram",
    label: "Instagram",
    aspectRatio: "1:1"   // Square format
  },
  {
    id: "twitter",
    label: "Twitter",
    aspectRatio: "16:9"  // Landscape format for timeline
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    aspectRatio: "16:9"  // Landscape format for feed
  },
  {
    id: "tiktok",
    label: "TikTok",
    aspectRatio: "3:4"   // Portrait format (closest to vertical)
  }
]

interface PostSettings {
  tone: string
  length: string
  language: string
  includeHashtags: boolean
  includeCta: boolean
  platform: string
}

export function SocialMediaPage({ profile, session }: { profile: any | null, session: any | null }) {
  const [selectedType, setSelectedType] = useState<typeof contentTypes[0] | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [generatedPost, setGeneratedPost] = useState<{
    content: string
    hashtags?: string[]
    imageUrl?: string
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'text' | 'hashtags' | null>(null)
  const [postSettings, setPostSettings] = useState<PostSettings>({
    tone: 'inspiring',
    length: 'medium',
    language: LANGUAGES[0].value,
    includeHashtags: true,
    includeCta: true,
    platform: 'facebook'
  })
  
  const { toast } = useToast()

  const handleTypeSelect = (type: typeof contentTypes[0]) => {
    setSelectedType(type)
    // Reset form when type changes
    setFormData({})
    setGeneratedPost(null)
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const copyToClipboard = async (text: string, type: 'text' | 'hashtags') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
      toast({
        title: "Copied!",
        description: `${type === 'text' ? 'Post content' : 'Hashtags'} copied to clipboard`
      })
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      })
    }
  }

  const handleSubmit = async () => {
    if (!selectedType) return
    
    setLoading(true)
    setError(null)

    try {
      // Generate social media post
      if (!session?.access_token) {
        throw new Error('No access token available')
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/social-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type: selectedType.id,
          context: formData,
          settings: {
            ...postSettings,
            platform: postSettings.platform || 'facebook'
          },
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate social media post')
      }

      const { data } = await response.json()

      // Generate image based on the post content
      const imageResponse = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt: `Create a social media image for ${postSettings.platform} that represents: ${data.content}`,
          aspectRatio: platforms.find(p => p.id === postSettings.platform)?.aspectRatio || '1:1',
          enhancePrompt: true
        })
      })

      if (!imageResponse.ok) {
        throw new Error('Failed to generate image')
      }

      const imageUrl = (await imageResponse.json()).data.imageUrl
      if (!imageUrl) throw new Error('No image URL in response')

      const finalPost = {
        ...data,
        imageUrl
      }

      setGeneratedPost(finalPost)

      toast({
        title: "Success!",
        description: "Your social media post has been generated.",
      })
    } catch (err) {
      console.error('Error:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate post')
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to generate post",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const getFormFields = () => {
    switch (selectedType?.id) {
      case 'impact_story':
        return [
          { id: 'beneficiary', label: 'Beneficiary Name/Description', type: 'input' },
          { id: 'challenge', label: 'Initial Challenge', type: 'textarea' },
          { id: 'solution', label: 'How You Helped', type: 'textarea' },
          { id: 'outcome', label: 'Outcome/Impact', type: 'textarea' }
        ]
      case 'fundraising_campaign':
        return [
          { id: 'campaign_name', label: 'Campaign Name', type: 'input' },
          { id: 'goal', label: 'Fundraising Goal', type: 'input' },
          { id: 'purpose', label: 'Purpose of Funds', type: 'textarea' },
          { id: 'urgency', label: 'Urgency/Timeline', type: 'input' }
        ]
      case 'volunteer_spotlight':
        return [
          { id: 'volunteer_name', label: 'Volunteer Name', type: 'input' },
          { id: 'duration', label: 'Time with Organization', type: 'input' },
          { id: 'contribution', label: 'Key Contributions', type: 'textarea' },
          { id: 'quote', label: 'Volunteer Quote (optional)', type: 'textarea' }
        ]
      // Add cases for other content types
      default:
        return [
          { id: 'title', label: 'Title/Topic', type: 'input' },
          { id: 'description', label: 'Description', type: 'textarea' },
          { id: 'key_points', label: 'Key Points', type: 'textarea' }
        ]
    }
  }

  const getPlatformImageSize = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return '1080x1080' // Square format
      case 'facebook':
        return '1200x630' // Landscape format
      case 'twitter':
        return '1200x675' // 16:9 format
      case 'linkedin':
        return '1200x627' // Recommended size
      default:
        return '1080x1080' // Default square format
    }
  }

  const getShareUrl = () => {
    // In a real app, this would be your app's URL where the post can be viewed
    return window.location.origin + '/share/post/' + Math.random().toString(36).substring(7)
  }

  const getShareTitle = () => {
    return generatedPost?.content.substring(0, 100) + '...'
  }

  return (
    <div className="space-y-6">
      <div className="mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Social Media Post Generator</h1>
          <p className="text-gray-600">Create engaging social media content optimized for your platform and purpose</p>
        </div>

        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <Label className="text-lg font-semibold mb-4">Content Type</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contentTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedType?.id === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                    onClick={() => handleTypeSelect(type)}
                  >
                    <h3 className="font-semibold mb-2">{type.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {type.platforms.map(platform => (
                        <span
                          key={platform}
                          className="text-xs px-2 py-1 bg-gray-100 rounded-full"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedType && (
              <>
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Post Details</Label>
                  {getFormFields().map((field) => (
                    <div key={field.id}>
                      <Label>{field.label}</Label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          value={formData[field.id] || ''}
                          onChange={(e) => handleInputChange(field.id, e.target.value)}
                          className="mt-1"
                        />
                      ) : (
                        <Input
                          value={formData[field.id] || ''}
                          onChange={(e) => handleInputChange(field.id, e.target.value)}
                          className="mt-1"
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Platform</Label>
                    <Select
                      value={postSettings.platform}
                      onValueChange={(value) =>
                        setPostSettings(prev => ({ ...prev, platform: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                      <SelectContent>
                        {platforms.map((platform) => (
                          <SelectItem key={platform.id} value={platform.id}>
                            <div className="space-y-1">
                              <div>{platform.label}</div>
                              <div className="text-xs text-gray-500">{platform.aspectRatio}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Language</Label>
                    <Select
                      value={postSettings.language}
                      onValueChange={(value) =>
                        setPostSettings(prev => ({ ...prev, language: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((language) => (
                          <SelectItem key={language.value} value={language.value}>
                            {language.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Tone & Style</Label>
                  <RadioGroup
                    value={postSettings.tone}
                    onValueChange={(value) =>
                      setPostSettings(prev => ({ ...prev, tone: value }))
                    }
                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="inspiring" id="inspiring" />
                      <Label htmlFor="inspiring">Inspiring</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="professional" id="professional" />
                      <Label htmlFor="professional">Professional</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="casual" id="casual" />
                      <Label htmlFor="casual">Casual</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="urgent" id="urgent" />
                      <Label htmlFor="urgent">Urgent</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={postSettings.includeHashtags}
                        onCheckedChange={(checked) =>
                          setPostSettings(prev => ({ ...prev, includeHashtags: checked }))
                        }
                      />
                      <Label>Include Hashtags</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={postSettings.includeCta}
                        onCheckedChange={(checked) =>
                          setPostSettings(prev => ({ ...prev, includeCta: checked }))
                        }
                      />
                      <Label>Include Call-to-Action</Label>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    onClick={handleSubmit}
                    disabled={loading || Object.keys(formData).length === 0}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Post'
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        {generatedPost && (
          <Card className="p-6 space-y-4">
            <h2 className="text-xl font-semibold">Generated Post</h2>
            
            {generatedPost.imageUrl ? (
              <div className="relative aspect-square w-full max-w-md mx-auto rounded-lg overflow-hidden bg-gray-100">
                <img 
                  src={generatedPost.imageUrl} 
                  alt="Generated social media image"
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    console.error('Image failed to load:', e);
                    const img = e.target as HTMLImageElement;
                    img.src = '/placeholder-image.jpg'; // You might want to add a placeholder image
                  }}
                />
              </div>
            ) : (
              <div className="relative aspect-square w-full max-w-md mx-auto rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <p className="text-gray-500">No image available</p>
              </div>
            )}

            <div className="space-y-4">
              <p className="whitespace-pre-wrap">{generatedPost.content}</p>
              
              {generatedPost.hashtags && generatedPost.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {generatedPost.hashtags.map((tag, index) => (
                    <span key={index} className="text-blue-500">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-4 pt-4">
                <FacebookShareButton url={getShareUrl()} quote={getShareTitle()}>
                  <FacebookIcon size={32} round />
                </FacebookShareButton>

                <TwitterShareButton url={getShareUrl()} title={getShareTitle()}>
                  <TwitterIcon size={32} round />
                </TwitterShareButton>

                <LinkedinShareButton url={getShareUrl()} title={getShareTitle()}>
                  <LinkedinIcon size={32} round />
                </LinkedinShareButton>

                <Button
                  onClick={() => {
                    const text = [
                      generatedPost.content,
                      generatedPost.hashtags?.join(' ')
                    ].filter(Boolean).join('\n\n')
                    
                    navigator.clipboard.writeText(text)
                    toast({
                      title: "Copied!",
                      description: "Post content copied to clipboard",
                    })
                  }}
                  variant="outline"
                >
                  Copy to Clipboard
                </Button>
              </div>
            </div>
          </Card>
        )}

        <LoadingDialog 
          open={loading} 
          title="Generating Post"
          description="Please wait while we create your social media content..."
        />
      </div>
    </div>
  )
}
